import { DownloadOutlined } from '@ant-design/icons';
import { Button, Drawer, Tag, message } from 'antd';
import {
  ProTable,
  type ProColumns,
  type ProFormInstance,
} from '@ant-design/pro-components';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resources } from '../i18n';
import { formatTenantDateTime } from '../lib/tenant';
type AuditEvent = {
  id: string;
  occurredAt: string;
  eventType: string;
  result: string;
  actorId?: string | null;
  actorUsername?: string | null;
  requestId?: string | null;
  objectType?: string | null;
  objectDisplayNo?: string | null;
  reason?: string | null;
  applicationVersion?: string | null;
  beforeData?: unknown;
  afterData?: unknown;
  changedFields?: unknown;
};

function isoDateTime(value: unknown): string | undefined {
  if (
    typeof value !== 'string' &&
    !dayjs.isDayjs(value) &&
    !(value instanceof Date)
  )
    return undefined;
  const date = dayjs(value);
  return date.isValid() ? date.toISOString() : undefined;
}

export function AuditPage() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<AuditEvent>();
  const formRef = useRef<ProFormInstance | undefined>(undefined);
  const [messageApi, holder] = message.useMessage();
  const buildSearch = (values: Record<string, unknown>) => {
    const out = new URLSearchParams();
    for (const [key, value] of [
      ['objectType', values.objectType],
      ['eventType', values.eventType],
      ['actorUsername', values.actorUsername],
    ] as const)
      if (typeof value === 'string' && value.trim()) out.set(key, value.trim());
    const range = values.occurredAtRange;
    if (Array.isArray(range)) {
      const from = isoDateTime(range[0] as unknown);
      const to = isoDateTime(range[1] as unknown);
      if (from) out.set('occurredFrom', from);
      if (to) out.set('occurredTo', to);
    }
    return out;
  };
  const requestEvents = async (values: Record<string, unknown>) => {
    try {
      const search = buildSearch(values);
      search.set('limit', '100');
      const response = await fetch(
        `/api/v1/audit-events?${search.toString()}`,
        { credentials: 'include' },
      );
      if (response.status === 401) {
        window.location.assign('/login');
        return { data: [], success: false, total: 0 };
      }
      if (response.status === 403) throw new Error(t('audit.forbidden'));
      if (!response.ok) throw new Error(t('audit.loadFailed'));
      const items = ((await response.json()) as { items: AuditEvent[] }).items;
      return { data: items, success: true, total: items.length };
    } catch (error) {
      await messageApi.error(
        error instanceof Error ? error.message : t('audit.loadFailed'),
      );
      return { data: [], success: false, total: 0 };
    }
  };
  const exportEvents = () => {
    const values = (formRef.current?.getFieldsValue?.() ?? {}) as Record<
      string,
      unknown
    >;
    window.location.assign(
      `/api/v1/audit-events/export.csv?${buildSearch(values).toString()}`,
    );
  };
  const eventTypeOptions = Object.keys(
    resources['zh-CN'].translation.audit.events,
  ).map((eventType) => ({
    value: eventType,
    label: `${t(`audit.events.${eventType}`)} (${eventType})`,
  }));
  const columns: ProColumns<AuditEvent>[] = [
    {
      title: t('audit.occurredAt'),
      dataIndex: 'occurredAt',
      width: 180,
      search: false,
      render: (_, row) => formatTenantDateTime(row.occurredAt),
    },
    {
      title: t('audit.occurredAt'),
      dataIndex: 'occurredAtRange',
      valueType: 'dateTimeRange',
      hideInTable: true,
    },
    {
      title: t('audit.objectType'),
      dataIndex: 'objectType',
      hideInTable: true,
      fieldProps: { placeholder: t('audit.objectTypePlaceholder') },
    },
    {
      title: t('audit.eventType'),
      dataIndex: 'eventType',
      width: 180,
      valueType: 'select',
      fieldProps: {
        options: eventTypeOptions,
        showSearch: true,
        allowClear: true,
        optionFilterProp: 'label',
      },
      render: (_, row) =>
        t(`audit.events.${row.eventType}`, {
          defaultValue: row.eventType,
        }),
    },
    {
      title: t('audit.actorUsername'),
      dataIndex: 'actorUsername',
      width: 140,
      render: (_, row) =>
        row.actorUsername ||
        (row.actorId ? t('audit.unknownActor') : t('audit.systemActor')),
    },
    {
      title: t('audit.result'),
      dataIndex: 'result',
      width: 90,
      search: false,
      render: (_, row) => (
        <Tag color={row.result === 'SUCCESS' ? 'green' : 'red'}>
          {t(`audit.results.${row.result}`, {
            defaultValue: t('audit.unknownResult'),
          })}
        </Tag>
      ),
    },
    {
      title: t('audit.object'),
      search: false,
      render: (_, row) => row.objectDisplayNo ?? row.objectType ?? '-',
    },
    {
      title: t('audit.reason'),
      dataIndex: 'reason',
      search: false,
      render: (_, row) => row.reason ?? '-',
    },
    {
      title: t('audit.actions'),
      valueType: 'option',
      search: false,
      fixed: 'right',
      width: 100,
      render: (_, row) => (
        <Button type="link" onClick={() => setSelected(row)}>
          {t('audit.detail')}
        </Button>
      ),
    },
  ];
  return (
    <section>
      {holder}
      <ProTable<AuditEvent>
        headerTitle={t('nav.audit')}
        formRef={formRef}
        request={requestEvents}
        search={{ labelWidth: 'auto', defaultCollapsed: false }}
        toolBarRender={() => [
          <Button
            key="export"
            icon={<DownloadOutlined />}
            onClick={exportEvents}
          >
            {t('audit.export')}
          </Button>,
        ]}
        cardBordered
        size="small"
        options={{
          reload: true,
          density: true,
          setting: true,
          fullScreen: true,
        }}
        rowKey="id"
        columns={columns}
        tableLayout="fixed"
        scroll={{ x: 1100 }}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
      />
      <Drawer
        title={t('audit.detail')}
        open={!!selected}
        onClose={() => setSelected(undefined)}
        width={640}
      >
        <p>
          <strong>{t('audit.requestId')}:</strong> {selected?.requestId ?? '-'}
        </p>
        <p>
          <strong>{t('audit.actorUsername')}:</strong>{' '}
          {selected?.actorUsername ||
            (selected?.actorId
              ? t('audit.unknownActor')
              : t('audit.systemActor'))}
        </p>
        <p>
          <strong>{t('audit.eventType')}:</strong>{' '}
          {selected
            ? t(`audit.events.${selected.eventType}`, {
                defaultValue: t('audit.unknownEvent'),
              })
            : '—'}{' '}
          {selected && <small>({selected.eventType})</small>}
        </p>
        <p>
          <strong>{t('audit.result')}:</strong>{' '}
          {selected
            ? t(`audit.results.${selected.result}`, {
                defaultValue: t('audit.unknownResult'),
              })
            : '—'}
        </p>
        <p>
          <strong>{t('audit.actorId')}:</strong> {selected?.actorId ?? '-'}
        </p>
        <p>
          <strong>{t('audit.applicationVersion')}:</strong>{' '}
          {selected?.applicationVersion ?? '-'}
        </p>
        <p>
          <strong>{t('audit.before')}:</strong>
        </p>
        <pre>{JSON.stringify(selected?.beforeData ?? {}, null, 2)}</pre>
        <p>
          <strong>{t('audit.after')}:</strong>
        </p>
        <pre>{JSON.stringify(selected?.afterData ?? {}, null, 2)}</pre>
      </Drawer>
    </section>
  );
}
